import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as faceapi from 'face-api.js';

const FaceDetection = ({ stream, onFaceAlert, isActive }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [isModelLoaded, setIsModelLoaded] = useState(false);
  const [isDetecting, setIsDetecting] = useState(false);
  const [faceStatus, setFaceStatus] = useState('loading');
  const alertCooldown = useRef(new Map());
  const lastAlertTime = useRef(0);

  // Load face-api models
  const loadModels = useCallback(async () => {
    const MODEL_URL = '/models'; // Models should be placed in public/models/
    
    try {
      console.log('Loading face-api models...');
      
      await Promise.all([
        faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
        faceapi.nets.faceExpressionNet.loadFromUri(MODEL_URL),
        faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL)
      ]);
      
      setIsModelLoaded(true);
      setFaceStatus('ready');
      console.log('Face-api models loaded successfully');
    } catch (error) {
      console.error('Error loading face-api models:', error);
      setFaceStatus('error');
      // Fallback: continue without face detection
      setIsModelLoaded(true);
    }
  }, []);

  // Initialize video element with stream
  useEffect(() => {
    if (stream && videoRef.current) {
      videoRef.current.srcObject = stream;
      videoRef.current.onloadedmetadata = () => {
        videoRef.current.play();
      };
    }
  }, [stream]);

  // Load models on component mount
  useEffect(() => {
    loadModels();
  }, [loadModels]);

  // Face detection function
  const detectFaces = useCallback(async () => {
    if (!videoRef.current || !canvasRef.current || !isModelLoaded || !isActive) {
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (video.readyState !== 4) return; // Video not ready

    try {
      // Detect faces with landmarks
      const detection = await faceapi
        .detectSingleFace(video, new faceapi.TinyFaceDetectorOptions())
        .withFaceLandmarks();

      // Clear canvas
      const displaySize = { width: video.videoWidth, height: video.videoHeight };
      faceapi.matchDimensions(canvas, displaySize);
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      if (detection) {
        setFaceStatus('detected');
        
        // Draw detection (optional - for debugging)
        // const resizedDetections = faceapi.resizeResults(detection, displaySize);
        // faceapi.draw.drawDetections(canvas, resizedDetections);
        // faceapi.draw.drawFaceLandmarks(canvas, resizedDetections);

        // Analyze face direction using landmarks
        const landmarks = detection.landmarks;
        analyzeFaceDirection(landmarks);
      } else {
        setFaceStatus('no-face');
        
        // Alert if no face detected for too long
        const now = Date.now();
        if (now - lastAlertTime.current > 5000) { // 5 seconds cooldown
          onFaceAlert('no-face', 1.0);
          lastAlertTime.current = now;
        }
      }
    } catch (error) {
      console.error('Face detection error:', error);
      setFaceStatus('error');
    }
  }, [isModelLoaded, isActive, onFaceAlert]);

  // Analyze face direction based on landmarks
  const analyzeFaceDirection = useCallback((landmarks) => {
    const nose = landmarks.getNose();
    const leftEye = landmarks.getLeftEye();
    const rightEye = landmarks.getRightEye();
    const mouth = landmarks.getMouth();

    if (!nose.length || !leftEye.length || !rightEye.length) return;

    // Calculate face center
    const noseTip = nose[3]; // Nose tip
    const leftEyeCenter = getPointsCenter(leftEye);
    const rightEyeCenter = getPointsCenter(rightEye);
    const mouthCenter = getPointsCenter(mouth);

    // Calculate horizontal deviation
    const eyeCenterX = (leftEyeCenter.x + rightEyeCenter.x) / 2;
    const horizontalDeviation = noseTip.x - eyeCenterX;
    const faceWidth = Math.abs(rightEyeCenter.x - leftEyeCenter.x);
    const horizontalRatio = Math.abs(horizontalDeviation) / faceWidth;

    // Calculate vertical deviation
    const eyeCenterY = (leftEyeCenter.y + rightEyeCenter.y) / 2;
    const verticalDeviation = noseTip.y - eyeCenterY;
    const faceHeight = Math.abs(mouthCenter.y - eyeCenterY);
    const verticalRatio = Math.abs(verticalDeviation) / faceHeight;

    // Thresholds for alerts
    const horizontalThreshold = 0.3;
    const verticalThreshold = 0.4;
    const minConfidence = 0.7;

    const now = Date.now();
    const cooldownPeriod = 3000; // 3 seconds between same-type alerts

    // Check horizontal movement (left/right)
    if (horizontalRatio > horizontalThreshold) {
      const direction = horizontalDeviation > 0 ? 'right' : 'left';
      const lastAlert = alertCooldown.current.get(direction) || 0;
      
      if (now - lastAlert > cooldownPeriod) {
        onFaceAlert(direction, minConfidence);
        alertCooldown.current.set(direction, now);
        console.log(`Face turning ${direction}, ratio: ${horizontalRatio.toFixed(2)}`);
      }
    }

    // Check vertical movement (up/down)
    if (verticalRatio > verticalThreshold) {
      const direction = verticalDeviation < 0 ? 'up' : 'down';
      const lastAlert = alertCooldown.current.get(direction) || 0;
      
      if (now - lastAlert > cooldownPeriod) {
        onFaceAlert(direction, minConfidence);
        alertCooldown.current.set(direction, now);
        console.log(`Face looking ${direction}, ratio: ${verticalRatio.toFixed(2)}`);
      }
    }
  }, [onFaceAlert]);

  // Helper function to get center point of landmarks
  const getPointsCenter = (points) => {
    const sum = points.reduce((acc, point) => ({
      x: acc.x + point.x,
      y: acc.y + point.y
    }), { x: 0, y: 0 });
    
    return {
      x: sum.x / points.length,
      y: sum.y / points.length
    };
  };

  // Start detection loop
  useEffect(() => {
    if (!isModelLoaded || !isActive) return;

    setIsDetecting(true);
    
    const detectionInterval = setInterval(detectFaces, 200); // Run every 200ms

    return () => {
      clearInterval(detectionInterval);
      setIsDetecting(false);
    };
  }, [isModelLoaded, isActive, detectFaces]);

  return (
    <div className="absolute inset-0 pointer-events-none">
      {/* Hidden video for face detection */}
      <video
        ref={videoRef}
        className="absolute inset-0 w-full h-full object-cover opacity-0"
        autoPlay
        muted
        playsInline
      />
      
      {/* Hidden canvas for face detection processing */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full opacity-0"
      />

      {/* Face Detection Status Indicator */}
      <div className="absolute bottom-4 right-4 pointer-events-auto">
        <div className="bg-gray-800 bg-opacity-90 backdrop-blur-sm rounded-lg p-3 shadow-lg">
          <div className="flex items-center space-x-2">
            <div className={`w-2 h-2 rounded-full ${
              faceStatus === 'detected' ? 'bg-green-500' :
              faceStatus === 'no-face' ? 'bg-yellow-500' :
              faceStatus === 'error' ? 'bg-red-500' :
              'bg-gray-500'
            }`}>
              {faceStatus === 'loading' && (
                <div className="w-2 h-2 rounded-full bg-gray-500 animate-pulse"></div>
              )}
            </div>
            <span className="text-xs text-gray-300">
              {faceStatus === 'detected' && 'Wajah Terdeteksi'}
              {faceStatus === 'no-face' && 'Wajah Tidak Terdeteksi'}
              {faceStatus === 'error' && 'Error Deteksi'}
              {faceStatus === 'loading' && 'Memuat Model...'}
              {faceStatus === 'ready' && 'Siap Deteksi'}
            </span>
          </div>
          
          {isDetecting && (
            <div className="mt-1 flex items-center space-x-1">
              <div className="w-1 h-1 bg-blue-400 rounded-full animate-pulse"></div>
              <span className="text-xs text-blue-300">Aktif</span>
            </div>
          )}
        </div>
      </div>

      {/* Alert when no face detected */}
      {faceStatus === 'no-face' && (
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 pointer-events-auto">
          <div className="bg-yellow-500 bg-opacity-90 text-yellow-900 px-6 py-3 rounded-lg shadow-lg">
            <div className="flex items-center space-x-2">
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <span className="font-semibold">Posisikan wajah Anda di depan kamera</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FaceDetection;